import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { Observable, Subject, interval, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { ScreenSize } from 'src/app/core/components/resize-detector/resize-detector.model';
import { UserEventSource } from 'src/app/core/constants/user-event.constants';
import { ContentBlockRegion, IContentBlock } from 'src/app/core/models/content.model';
import { IPartner, IPartnerListResponse } from 'src/app/core/models/partner.response';
import { GraphQLService } from 'src/app/core/services/graphql.service';
import { ResizeService } from 'src/app/core/services/resize.service';
import { IContentBlockComponent } from 'src/app/features/content-blocks/content-block-component.model';
import { partnerListQuery } from './partners-list.query';

const partnerAnimation = trigger('partner', [
  state('out', style({ opacity: 0 })),
  state('in', style({ opacity: 1 })),
  transition('* => out', animate('1100ms ease-out')),
  transition('* => in', animate('1100ms ease-in'))
]);

@Component({
  selector: 'air-partners-list',
  templateUrl: './partners-list.component.html',
  animations: [partnerAnimation]
})
export class PartnersListComponent implements IContentBlockComponent, OnInit, OnDestroy {
  className = 'partner-list';
  region?: ContentBlockRegion;
  content: IContentBlock;
  isFirst?: boolean;
  userEventSource = UserEventSource.partnerLogos;

  destroy$ = new Subject();
  partners: IPartner[] = [];
  loading = true;
  loadingError = false;
  displayPartners: IPartner[] = [];
  interval?: Observable<number>;
  isAnimating = false;

  @HostListener('window:resize', [])
  onResize(): void {
    this.detectScreenSize();
  }

  constructor(
    private graphQLService: GraphQLService,
    private resizeService: ResizeService
  ) {}

  ngOnInit(): void {
    this.graphQLService
      .watchQuery<IPartnerListResponse>(partnerListQuery, undefined, true)
      .pipe(
        catchError(() => {
          this.loading = false;
          this.loadingError = true;
          return of({ data: null, loading: false } as ApolloQueryResult<null>);
        })
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (!result?.data?.allPartner?.items?.length) {
          return;
        }

        this.partners = result.data.allPartner.items;
        this.loading = result.loading;
        this.detectScreenSize();
      });
  }

  ngOnDestroy(): void {
    this.interval = undefined;
    this.destroy$.next(void 0);
    this.destroy$.complete();
  }

  detectScreenSize(): void {
    const size = this.resizeService.getCurrentSize();
    if (size === ScreenSize.sm || size === ScreenSize.xsm) {
      if (!this.interval) {
        this.setPartnerList();
        this.setPartnerListAnimationLoop();
      }
    } else {
      this.interval = undefined;
      this.destroy$.next(void 0);
    }
  }

  setPartnerListAnimationLoop(): void {
    this.interval = interval(5000).pipe(takeUntil(this.destroy$));
    this.interval.subscribe(() => {
      this.isAnimating = true;
      setTimeout(() => {
        this.setPartnerList();
        this.isAnimating = false;
      }, 1300);
    });
  }

  setPartnerList(): void {
    this.displayPartners = this.partners.slice(0, 5);
    const partnerList = [...this.partners];
    this.partners = partnerList.splice(5).concat(this.displayPartners);
  }
}
